import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChartLineUp, LockKey } from "@phosphor-icons/react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

function SummaryCard({ label, value, accent, testId }) {
  return (
    <div className={`border rounded-md p-3 card-lift ${accent ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20" : "border-border bg-card"}`} data-testid={testId}>
      <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${accent ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>{label}</p>
      <p className={`text-xl font-bold font-[Chivo] ${accent ? "text-emerald-700 dark:text-emerald-400" : ""}`}>{value}</p>
    </div>
  );
}

function MarkupTable({ quotes, summary: s }) {
  const th = "px-3 py-2 font-semibold text-[10px] uppercase tracking-wider text-muted-foreground";
  return (
    <div className="border border-border rounded-md overflow-x-auto" data-testid="dash-markup-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/30 border-b border-border">
            <th className={`${th} text-left`}>Reference</th>
            <th className={`${th} text-left`}>Client</th>
            <th className={`${th} text-left`}>Supplier</th>
            <th className={`${th} text-right`}>Cost</th>
            <th className={`${th} text-center`}>Markup A</th>
            <th className={`${th} text-center`}>Markup B</th>
            <th className={`${th} text-right`}>Total</th>
            <th className={`${th} text-right !text-emerald-600`}>Profit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {quotes.map((q, i) => (
            <tr key={i} className="hover:bg-muted/10 transition-colors" data-testid={`markup-row-${i}`}>
              <td className="px-3 py-2 font-mono">{q.jr_reference}</td>
              <td className="px-3 py-2 text-muted-foreground">{q.client_name || "-"}</td>
              <td className="px-3 py-2">{q.vendor_name}</td>
              <td className="px-3 py-2 text-right font-mono">{gbp(q.cost)}</td>
              <td className="px-3 py-2 text-center"><span className="text-primary font-medium">{q.markup_a_pct}%</span> <span className="text-muted-foreground">({gbp(q.markup_a_amount)})</span></td>
              <td className="px-3 py-2 text-center"><span className="text-violet-600 dark:text-violet-400 font-medium">{q.markup_b_pct}%</span> <span className="text-muted-foreground">({gbp(q.markup_b_amount)})</span></td>
              <td className="px-3 py-2 text-right font-mono font-semibold">{gbp(q.total)}</td>
              <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">{gbp(q.profit)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-muted/20 border-t-2 border-border font-semibold">
            <td colSpan={3} className="px-3 py-2 text-xs uppercase">Totals</td>
            <td className="px-3 py-2 text-right font-mono text-xs">{gbp(s.total_cost)}</td>
            <td className="px-3 py-2 text-center font-mono text-xs text-primary">{gbp(s.total_markup_a)}</td>
            <td className="px-3 py-2 text-center font-mono text-xs text-violet-600 dark:text-violet-400">{gbp(s.total_markup_b)}</td>
            <td className="px-3 py-2 text-right font-mono text-xs">{gbp(s.total_client_price)}</td>
            <td className="px-3 py-2 text-right font-mono text-xs text-emerald-600 dark:text-emerald-400">{gbp(s.total_profit)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export default function MarkedUpQuotesPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.operations_director) { setLoading(false); return; }
    API.get("/markup-analytics")
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load markup analytics"))
      .finally(() => setLoading(false));
  }, [user?.operations_director]);

  if (!user?.operations_director) {
    return (
      <div className="max-w-xl mx-auto mt-16 text-center space-y-3" data-testid="markup-page-restricted">
        <LockKey size={36} className="mx-auto text-muted-foreground" />
        <h1 className="text-xl font-bold font-[Chivo]">Operations Director access only</h1>
        <p className="text-sm text-muted-foreground">Marked-up quote margins are restricted to Operations Directors. Ask an Operations Director to grant you the flag in Settings → Users if you need it.</p>
        <Link to="/" className="text-sm text-primary hover:underline">Back to dashboard</Link>
      </div>
    );
  }

  const s = data?.summary || {};
  const quotes = data?.quotes || [];
  return (
    <div className="space-y-6 animate-fade-in" data-testid="marked-up-quotes-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight flex items-center gap-2"><ChartLineUp size={26} className="text-emerald-600" /> Marked-Up Quotes</h1>
          <p className="text-sm text-muted-foreground mt-1">Supplier cost vs client price across every marked-up quote — Operations Director view.</p>
        </div>
        <Link to="/settings#markup" className="text-xs text-primary hover:underline" data-testid="markup-manage-templates-link">Manage markup templates</Link>
      </div>

      {loading && <p className="text-sm text-muted-foreground" data-testid="markup-loading">Loading…</p>}
      {error && <p className="text-sm text-red-600" data-testid="markup-error">{error}</p>}
      {!loading && !error && !quotes.length && (
        <div className="border border-dashed border-border rounded-lg p-10 text-center text-sm text-muted-foreground" data-testid="markup-empty">
          No marked-up quotes yet. Apply a markup template to a supplier quote on a Job Request and it will appear here.
        </div>
      )}
      {!loading && quotes.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Our Profit" value={gbp(s.total_profit)} accent testId="dash-markup-profit" />
            <SummaryCard label="Total Cost" value={gbp(s.total_cost)} testId="dash-markup-cost" />
            <SummaryCard label="Client Price" value={gbp(s.total_client_price)} testId="dash-markup-client" />
            <SummaryCard label="Avg Margin" value={`${s.avg_margin_pct ?? 0}%`} testId="dash-markup-margin" />
          </div>
          <MarkupTable quotes={quotes} summary={s} />
        </>
      )}
    </div>
  );
}
